/**
 * TypeScript types for entities store module
 * Defines all type-safe interfaces for state, entities, and relationships
 */

// ============================================================================
// Entity Types (aligned with DTOs)
// ============================================================================

export interface HomeEntity {
  uid: string;
  name: string;
  userUid: string;
  deviceUids: string[];
  gatewayUids: string[];
}

export interface RoomEntity {
  uid: string;
  name: string;
  homeUid: string;
  devices: any[]; // Raw device objects from API
  gateways: any[]; // Raw gateway objects from API
}

export interface DeviceEntity {
  id: string;
  name: string;
  deviceAppName: string | null;
  gatewayId: string | null;
  deviceInfo: any;
}

export interface GatewayEntity {
  uid: string;
  groupId: string;
  name: string;
  gatewayAppName: string | null;
  deviceIds: string[];
  status?: 'REGISTERED' | 'DEREGISTERED' | 'UNKNOWN';
}

// ============================================================================
// Last Telemetry Data
// ============================================================================

export interface LastTelemetryData {
  timestamp: number;
  temperature?: number;
  humidity?: number;
  pressure?: number;
  co2?: number;
  tvoc?: number;
  radar?: number | import('@/types/radar').RadarTarget[];
  rssi?: number;
  lqi?: number;
  [key: string]: any; // Allow additional fields
}

// ============================================================================
// Relationships Structure
// ============================================================================

export interface Relationships {
  deviceToGateway: Record<string, string>;    // { [deviceId]: gatewayId }
  deviceToRoom: Record<string, string>;       // { [deviceId]: roomId }
  deviceToHome: Record<string, string>;       // { [deviceId]: homeId }
  gatewayToHome: Record<string, string>;      // { [gatewayId]: homeId }
  roomToHome: Record<string, string>;         // { [roomId]: homeId }
  homeToRooms: Record<string, string[]>;      // { [homeId]: [roomIds] }
  homeToDevices: Record<string, string[]>;    // { [homeId]: [deviceIds] }
  homeToGateways: Record<string, string[]>;   // { [homeId]: [gatewayIds] }
  roomToDevices: Record<string, string[]>;    // { [roomId]: [deviceIds] }
  gatewayToDevices: Record<string, string[]>; // { [gatewayId]: [deviceIds] }
}

// ============================================================================
// Normalized Data Structure
// ============================================================================

export interface NormalizedData {
  homes: Record<string, HomeEntity>;
  rooms: Record<string, RoomEntity>;
  devices: Record<string, DeviceEntity>;
  gateways: Record<string, GatewayEntity>;
  relationships: Relationships;
}

// ============================================================================
// State Type
// ============================================================================

export interface EntitiesState {
  // Normalized entities stored by ID
  homes: Record<string, HomeEntity>;
  rooms: Record<string, RoomEntity>;
  devices: Record<string, DeviceEntity>;
  gateways: Record<string, GatewayEntity>;

  // Relationship mappings for cross-referencing
  relationships: Relationships;

  // Device telemetry (Last telemetry data for fast device switching)
  deviceTelemetry: Record<string, LastTelemetryData>;

  // Metadata
  photoUrlByHome: Record<string, string>;

  // Cache control
  lastFetched: number | null;
  cacheVersion: string;
}

// ============================================================================
// Helper: Empty Relationships
// ============================================================================

export function createEmptyRelationships(): Relationships {
  return {
    deviceToGateway: {},
    deviceToRoom: {},
    deviceToHome: {},
    gatewayToHome: {},
    roomToHome: {},
    homeToRooms: {},
    homeToDevices: {},
    homeToGateways: {},
    roomToDevices: {},
    gatewayToDevices: {},
  };
}
